import React from 'react';
import { HeaderSearchInput } from "@/components/layouts/header-slots";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { usePermissions } from "@/hooks/use-permissions";
import type { ProjectHeaderSearchProps, ProjectHeaderTeamSearchProps } from '../types/project-details.types';

interface ProjectHeaderProps {
  project: any;
  globalTicketSearchTerm: string;
  setGlobalTicketSearchTerm: (term: string) => void;
  globalTeamSearchTerm: string;
  setGlobalTeamSearchTerm: (term: string) => void;
  activeTab: string;
  onBackToProjects: () => void;
  onSaveAndReturn: () => void;
  currentUser?: any;
  isTicketFormActive: boolean;
  setIsTicketFormActive: (active: boolean) => void;
}

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  project,
  globalTicketSearchTerm,
  setGlobalTicketSearchTerm,
  globalTeamSearchTerm,
  setGlobalTeamSearchTerm,
  activeTab,
  onBackToProjects,
  onSaveAndReturn,
  currentUser,
  isTicketFormActive,
  setIsTicketFormActive,
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const { hasPermission } = usePermissions();

  const handleBackToProjects = () => {
    onBackToProjects();
  };

  const handleSaveAndReturn = () => {
    onSaveAndReturn();
  };

  const handleNewTicket = () => {
    setIsTicketFormActive(true);
  };

  const shouldShowNewTicketButton = () => {
    return hasPermission('manage_projects') || hasPermission('manage_all_projects');
  };

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Left side - Project info and navigation */}
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToProjects}
            className="text-slate-600 hover:text-slate-800"
          >
            ← {t('common:back')}
          </Button>
          
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold text-slate-900">
              {project?.name || 'Loading...'}
            </h1>
            {project?.projectNumber && (
              <span className="text-sm text-slate-500">
                ({project.projectNumber})
              </span>
            )}
          </div>
        </div>

        {/* Center - Search components based on active tab */}
        <div className="flex-1 flex justify-center">
          {activeTab === 'tickets' && (
            <ProjectHeaderSearch
              searchTerm={globalTicketSearchTerm}
              setSearchTerm={setGlobalTicketSearchTerm}
            />
          )}
          {activeTab === 'team' && (
            <ProjectHeaderTeamSearch
              searchTerm={globalTeamSearchTerm}
              setSearchTerm={setGlobalTeamSearchTerm}
            />
          )}
        </div>

        {/* Right side - Action buttons */}
        <div className="flex items-center space-x-2">
          {/* New ticket button - only show for main users */}
          {shouldShowNewTicketButton() && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                void handleNewTicket();
              }}
              className="text-xs bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('tickets.newTicket')}
            </Button>
          )}

          {/* Save and return button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveAndReturn}
            className="text-xs"
          >
            {t('common:saveAndReturn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Search component for tickets
export const ProjectHeaderSearch: React.FC<ProjectHeaderSearchProps> = ({
  searchTerm,
  setSearchTerm,
}) => {
  const { t } = useTranslation(['pages.projectDetails']);

  return (
    <HeaderSearchInput
      value={searchTerm}
      onChange={setSearchTerm}
      placeholder={t('tickets.searchPlaceholder')}
    />
  );
};

// Search component for team
export const ProjectHeaderTeamSearch: React.FC<ProjectHeaderTeamSearchProps> = ({
  searchTerm,
  setSearchTerm,
}) => {
  const { t } = useTranslation(['pages.projectDetails']);

  return (
    <HeaderSearchInput
      value={searchTerm}
      onChange={setSearchTerm}
      placeholder={t('team.searchPlaceholder')}
    />
  );
};
